// AES-256-GCM payload encryption shared with the backend's PayloadEncryptionInterceptor.
// Wire format: base64(iv[12] + authTag[16] + ciphertext).
// NOTE: the key ships in the browser bundle, so this is obfuscation + integrity, not secrecy.

const KEY_HEX = (import.meta.env.VITE_API_AES_KEY || '').trim();

export const isEncryptionEnabled =
  import.meta.env.VITE_ENABLE_ENCRYPTION === 'true' && /^[0-9a-fA-F]{64}$/.test(KEY_HEX);

if (import.meta.env.VITE_ENABLE_ENCRYPTION === 'true' && !isEncryptionEnabled) {
  console.warn('VITE_API_AES_KEY must be exactly 64 hex characters; payload encryption disabled.');
}

const hexToBytes = (hex: string) =>
  Uint8Array.from(hex.match(/.{2}/g)!.map((b) => parseInt(b, 16)));

let keyPromise: Promise<CryptoKey> | null = null;
const getKey = () =>
  (keyPromise ??= crypto.subtle.importKey('raw', hexToBytes(KEY_HEX), { name: 'AES-GCM' }, false, [
    'encrypt',
    'decrypt',
  ]));

const toB64 = (u8: Uint8Array) => {
  let s = '';
  u8.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s);
};

const fromB64 = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

export async function encryptPayload(data: unknown): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plain = new TextEncoder().encode(JSON.stringify(data));
  // WebCrypto returns ciphertext + tag (tag last); the wire format wants the tag right after the IV
  const out = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, await getKey(), plain)
  );
  const ct = out.slice(0, out.length - 16);
  const tag = out.slice(out.length - 16);
  const wire = new Uint8Array(12 + 16 + ct.length);
  wire.set(iv, 0);
  wire.set(tag, 12);
  wire.set(ct, 28);
  return toB64(wire);
}

export async function decryptPayload<T = unknown>(b64: string): Promise<T> {
  const wire = fromB64(b64);
  if (wire.length < 29) throw new Error('Payload too short');
  const iv = wire.slice(0, 12);
  const tag = wire.slice(12, 28);
  const ct = wire.slice(28);
  const buf = new Uint8Array(ct.length + 16);
  buf.set(ct, 0);
  buf.set(tag, ct.length);
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv, tagLength: 128 },
    await getKey(),
    buf
  );
  return JSON.parse(new TextDecoder().decode(plain)) as T;
}

// Plain JSON objects/arrays only — FormData, Blob, strings etc. are sent as-is
export const isJsonBody = (body: unknown): boolean =>
  body !== null &&
  typeof body === 'object' &&
  !(body instanceof FormData) &&
  !(body instanceof Blob) &&
  !(body instanceof ArrayBuffer) &&
  !(body instanceof URLSearchParams);

// Encrypted responses are exactly { payload: "<base64>" }
export const isEncryptedResponse = (data: unknown): data is { payload: string } =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as { payload?: unknown }).payload === 'string' &&
  Object.keys(data).length === 1;
